import { JSX, useEffect, useRef, useState } from 'react';
import {
  Controller,
  FieldPath,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { Grid } from 'components/layout';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import {
  LoadDateInput,
  LoadDateTimeInput,
} from 'pages/LoadView/LoadInformation/Components';
import {
  LoadStopTypes,
  LoadStopsItem,
  LoadStopsItemTypes,
  NormalizedLoad,
  TMSLocation,
} from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

import { AILocationHintBanner } from '../components/AiLocationHintBanner';
import { CreateLocationButton } from '../components/CreateLocationButton';
import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import LocationCreatorDialog from '../components/LocationCreatorDialog';
import { TURVO_STOP_ACTION_OPTIONS } from './constants';
import {
  applySelectedLocationToTurvoRouteStop,
  isTurvoDropoffStopType,
  isTurvoPickupStopType,
  toMaybeDate,
} from './turvoRouteStops';
import { hasValidAISuggestedLocation } from './utils';

export type TurvoRouteStopFormProps = {
  stopIndex: number;
  stopType: LoadStopsItemTypes;
  formMethods: UseFormReturn<NormalizedLoad>;
  allowStopActionSelect?: boolean;
  allowLocationSelect?: boolean;
  /**
   * Turvo's update API only persists date/appointment/reference fields on the
   * route ends. Intermediate stops keep those fields read-only outside create.
   */
  allowPersistedFieldEdits?: boolean;
  isLoadingLocations: boolean;
  locations: Maybe<TMSLocation[]>;
  handleRefreshLocations: () => void;
  tmsID: number;
  setLocations: (locations: TMSLocation[]) => void;
  originalSuggestionData?: NormalizedLoad;
  /** The suggestion field backing this stop, set only for the route ends. */
  suggestionStopType?: LoadStopTypes;
};

export function TurvoRouteStopForm({
  stopIndex,
  stopType: stopTypeProp,
  formMethods,
  allowStopActionSelect = false,
  allowLocationSelect = true,
  allowPersistedFieldEdits = true,
  isLoadingLocations,
  locations,
  handleRefreshLocations,
  tmsID,
  setLocations,
  originalSuggestionData,
  suggestionStopType,
}: TurvoRouteStopFormProps): JSX.Element {
  const { control, getValues, setValue, formState, trigger, clearErrors } =
    formMethods;
  const stopKey = `stops.${stopIndex}` as const;
  const stopTypePath = `${stopKey}.stopType` as FieldPath<NormalizedLoad>;
  const watchedStopType = useWatch({ control, name: stopTypePath });
  const stopType = (watchedStopType || stopTypeProp) as LoadStopsItemTypes;
  const isPickupWindow = isTurvoPickupStopType(stopType);

  const [isLocationCreatorOpen, setIsLocationCreatorOpen] = useState(false);
  const [filteredLocations, setFilteredLocations] =
    useState<Maybe<TMSLocation[]>>(locations);

  const { aiDefaultValues } = useExtendedFormContext();
  const fieldAttrs = useFieldAttributes();
  // Route ends use flat pickup/consignee attrs (same as the old Stop form);
  // intermediate stops fall back to the stops.* template.
  const refNumberAttrPath = suggestionStopType
    ? `${suggestionStopType}.refNumber`
    : `${stopKey}.refNumber`;
  const refNumberAttr = getFieldAttribute(fieldAttrs, refNumberAttrPath);
  const watchedLocationID = useWatch({
    control,
    name: `${stopKey}.externalTMSID` as FieldPath<NormalizedLoad>,
  });
  const watchedStop = useWatch({
    control,
    name: stopKey as FieldPath<NormalizedLoad>,
  }) as MaybeUndef<LoadStopsItem>;
  const watchedLoad = getValues();
  const previousStopTypeRef = useRef(stopType);

  // A suggested location is created on submit, so requiring the select here
  // would fail RHF validation before createAISuggestedLocations can fill it in.
  const hasValidAISuggestion =
    !!suggestionStopType &&
    !watchedLocationID &&
    hasValidAISuggestedLocation(
      suggestionStopType,
      watchedLoad,
      originalSuggestionData
    );

  useEffect(() => {
    setFilteredLocations(locations);
  }, [locations]);

  useEffect(() => {
    if (!watchedLocationID) {
      return;
    }

    const selectedLoc = filteredLocations?.find(
      (loc) => loc.externalTMSID === watchedLocationID
    );
    if (!selectedLoc) {
      return;
    }

    const current = getValues(
      stopKey as FieldPath<NormalizedLoad>
    ) as LoadStopsItem;
    const next = applySelectedLocationToTurvoRouteStop(current, selectedLoc);
    setValue(stopKey as FieldPath<NormalizedLoad>, next, {
      shouldDirty: aiDefaultValues,
    });
  }, [
    watchedLocationID,
    filteredLocations,
    aiDefaultValues,
    getValues,
    setValue,
    stopKey,
  ]);

  // Appointment start is only required once an end time exists, so the rule has
  // to be re-evaluated whenever the end time appears or clears.
  useEffect(() => {
    const startPath = `${stopKey}.apptStartTime` as FieldPath<NormalizedLoad>;

    if (watchedStop?.apptEndTime) {
      void trigger(startPath);
    } else {
      clearErrors(startPath);
    }
  }, [watchedStop, stopKey, trigger, clearErrors]);

  useEffect(() => {
    if (!allowStopActionSelect) {
      previousStopTypeRef.current = stopType;
      return;
    }

    const previous = previousStopTypeRef.current;
    previousStopTypeRef.current = stopType;

    if (previous === stopType) {
      return;
    }

    if (isTurvoDropoffStopType(stopType) && isTurvoPickupStopType(previous)) {
      const readyTime = getValues(
        `${stopKey}.readyTime` as FieldPath<NormalizedLoad>
      );
      const mustDeliver = getValues(
        `${stopKey}.mustDeliver` as FieldPath<NormalizedLoad>
      );
      if (readyTime && !mustDeliver) {
        setValue(
          `${stopKey}.mustDeliver` as FieldPath<NormalizedLoad>,
          readyTime,
          { shouldDirty: true }
        );
      }
      setValue(`${stopKey}.readyTime` as FieldPath<NormalizedLoad>, '', {
        shouldDirty: true,
      });
      return;
    }

    if (isTurvoPickupStopType(stopType) && isTurvoDropoffStopType(previous)) {
      const mustDeliver = getValues(
        `${stopKey}.mustDeliver` as FieldPath<NormalizedLoad>
      );
      const readyTime = getValues(
        `${stopKey}.readyTime` as FieldPath<NormalizedLoad>
      );
      if (mustDeliver && !readyTime) {
        setValue(
          `${stopKey}.readyTime` as FieldPath<NormalizedLoad>,
          mustDeliver,
          { shouldDirty: true }
        );
      }
      setValue(`${stopKey}.mustDeliver` as FieldPath<NormalizedLoad>, '', {
        shouldDirty: true,
      });
    }
  }, [allowStopActionSelect, getValues, setValue, stopKey, stopType]);

  const handleLocationCreated = (newLocation: TMSLocation) => {
    const updatedLocations = [...(locations || []), newLocation];
    setLocations(updatedLocations);
    setFilteredLocations(updatedLocations);
    setValue(
      `${stopKey}.externalTMSID` as FieldPath<NormalizedLoad>,
      newLocation.externalTMSID,
      { shouldDirty: true }
    );
  };

  return (
    <>
      <AILocationHintBanner isVisible={hasValidAISuggestion} />

      {allowStopActionSelect ? (
        <div>
          <Label name={stopTypePath} required>
            Stop Action
          </Label>
          <Controller
            name={stopTypePath}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={
                  isTurvoDropoffStopType(field.value as string)
                    ? LoadStopsItemTypes.Dropoff
                    : LoadStopsItemTypes.Pickup
                }
              >
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Choose' />
                </SelectTrigger>
                <SelectContent>
                  {TURVO_STOP_ACTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      ) : null}

      <RHFLocationSelect
        required={!hasValidAISuggestion}
        name={`${stopKey}.externalTMSID`}
        label='Location'
        control={control}
        errors={formState.errors}
        locations={filteredLocations}
        setLocations={setFilteredLocations}
        isLoadingLocations={isLoadingLocations}
        handleRefreshLocations={handleRefreshLocations}
        labelIsName={true}
        disabled={!allowLocationSelect}
      />

      {allowLocationSelect && (
        <CreateLocationButton
          onClick={() => setIsLocationCreatorOpen(true)}
          disabled={isLoadingLocations}
        />
      )}

      <LoadBuildingTextInput
        name={`${stopKey}.address.name`}
        label='Name'
        readOnly={true}
      />

      <LoadBuildingTextInput
        name={`${stopKey}.address.addressLine1`}
        label='Address Line 1'
        readOnly={true}
      />

      <LoadBuildingTextInput
        name={`${stopKey}.address.addressLine2`}
        label='Address Line 2'
        readOnly={true}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name={`${stopKey}.address.city`}
          label='City'
          readOnly={true}
        />
        <LoadBuildingTextInput
          name={`${stopKey}.address.state`}
          label='State'
          readOnly={true}
        />
      </Grid>

      <LoadBuildingTextInput
        name={`${stopKey}.address.zip`}
        label='Zip Code'
        placeholder='12345'
        readOnly={true}
      />

      {isPickupWindow ? (
        <LoadDateInput
          name={`${stopKey}.readyTime` as FieldPath<NormalizedLoad>}
          label='Pickup Date'
          required
          readOnly={!allowPersistedFieldEdits}
        />
      ) : (
        <LoadDateInput
          name={`${stopKey}.mustDeliver` as FieldPath<NormalizedLoad>}
          label='Delivery Date'
          required
          readOnly={!allowPersistedFieldEdits}
        />
      )}

      <div>
        <Label name={`${stopKey}.apptType`}>Appointment Type</Label>
        <Controller
          name={`${stopKey}.apptType` as FieldPath<NormalizedLoad>}
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={(field.value as string) || ''}
              disabled={!allowPersistedFieldEdits}
            >
              <SelectTrigger className='w-full mt-1'>
                <SelectValue placeholder='Choose' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='FCFS'>FCFS</SelectItem>
                <SelectItem value='By appointment'>By appointment</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <Controller
        name={`${stopKey}.apptRequired` as FieldPath<NormalizedLoad>}
        control={control}
        render={({ field }) => (
          <Checkbox
            label='Appointment Required?'
            labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
            checked={(field.value as boolean | undefined) ?? false}
            disabled={!allowPersistedFieldEdits}
            onCheckedChange={(checked) => field.onChange(checked)}
          />
        )}
      />

      <LoadDateTimeInput
        name={`${stopKey}.apptStartTime` as FieldPath<NormalizedLoad>}
        label='Appointment Start Time'
        load={watchedLoad}
        defaultDate={
          isPickupWindow
            ? toMaybeDate(watchedStop?.readyTime)
            : toMaybeDate(watchedStop?.mustDeliver)
        }
        rules={{
          required: watchedStop?.apptEndTime ? 'Required' : false,
        }}
        readOnly={!allowPersistedFieldEdits}
      />

      <LoadDateTimeInput
        name={`${stopKey}.apptEndTime` as FieldPath<NormalizedLoad>}
        label='Appointment End Time'
        load={watchedLoad}
        defaultDate={
          isPickupWindow
            ? toMaybeDate(watchedStop?.readyTime)
            : toMaybeDate(watchedStop?.mustDeliver)
        }
        readOnly={!allowPersistedFieldEdits}
      />

      {!refNumberAttr?.isNotSupported && (
        <LoadRefPillInput
          name={`${stopKey}.refNumber` as FieldPath<NormalizedLoad>}
          control={control}
          label={isPickupWindow ? 'Pickup #' : 'Delivery #'}
          originalSuggestionData={originalSuggestionData || getValues()}
          disabled={Boolean(
            !allowPersistedFieldEdits || refNumberAttr?.isReadOnly
          )}
        />
      )}

      <LoadBuildingTextInput
        name={`${stopKey}.apptNum`}
        label='Appointment Number'
        placeholder='Appointment Number'
        readOnly={!allowPersistedFieldEdits}
      />

      <LoadBuildingTextInput
        name={`${stopKey}.apptNote`}
        label='Appointment Note'
        placeholder='Appointment Note'
        readOnly={!allowPersistedFieldEdits}
      />

      <LocationCreatorDialog
        isOpen={isLocationCreatorOpen}
        onClose={() => setIsLocationCreatorOpen(false)}
        onLocationCreated={handleLocationCreated}
        tmsID={tmsID}
      />
    </>
  );
}
